import React, { Ref, useState, useEffect } from 'react';
import { Select } from 'antd';
import { BaseSelectRef } from 'rc-select';
import { useTranslation } from 'react-i18next';
import { omit } from 'lodash';

import { EPButton } from '..';
import { StyledDrawer } from './EPSelect.styles';
import EPMobileSelectList from './EPMobileSelectList/EPMobileSelectList';

const { Option } = Select;

interface EPSelectProps {
  shouldRenderDrawer?: boolean;
  drawerTitle?: string;
  drawerSubmitText?: string;
  onChange?: (value: string | string[]) => void;
  options?: { value: string; label: string }[];
  selectedValue?: string | string[];
  disabled?: boolean;
  loading?: boolean;
  [x: string]: unknown;
}

const EPSelect = React.forwardRef((props: EPSelectProps, ref: Ref<BaseSelectRef>) => {
  const { shouldRenderDrawer, drawerTitle, drawerSubmitText, onChange, options, disabled, loading, selectedValue } =
    props;
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(selectedValue);
  const { t } = useTranslation();

  useEffect(() => {
    setSelectedOption(selectedValue);
  }, [selectedValue]);

  const onOpenMobileDrawer = () => setIsMobileDrawerOpen(true);
  const onCloseMobileDrawer = () => setIsMobileDrawerOpen(false);

  const onMobileDrawerSubmit = () => {
    onChange && onChange(selectedOption);
    setIsMobileDrawerOpen(false);
  };

  const onMenuSelect = (key: string) => {
    if (Array.isArray(selectedOption)) {
      setSelectedOption(selectedOption =>
        selectedOption.includes(key) ? selectedOption.filter(o => o !== key) : [...selectedOption, key],
      );
    } else {
      setSelectedOption(key);
    }
  };

  const relevantProps = omit(props, 'shouldRenderDrawer', 'drawerTitle', 'drawerSubmitText', 'selectedValue');
  const additionalProps =
    shouldRenderDrawer && !disabled && !loading ? { onClick: onOpenMobileDrawer, open: false } : {};

  return (
    <>
      <Select size="large" {...relevantProps} ref={ref} {...additionalProps} />
      {isMobileDrawerOpen && (
        <StyledDrawer
          placement="bottom"
          onClose={onCloseMobileDrawer}
          title={drawerTitle}
          open={isMobileDrawerOpen}
          destroyOnClose
          classNames={{ header: 'select-drawer-header', footer: 'select-drawer-footer' }}
          footer={
            <EPButton type="primary" onClick={onMobileDrawerSubmit} className="select-drawer-footer-button">
              {drawerSubmitText ?? t('Continue')}
            </EPButton>
          }
        >
          <EPMobileSelectList
            options={options || []}
            selectedValue={selectedOption}
            onSelect={onMenuSelect}
            height={230}
            itemHeight={32}
          />
        </StyledDrawer>
      )}
    </>
  );
});

export default EPSelect;
export const EPSelectOption = Option;
